import { trace } from '@opentelemetry/api';
import { IntlProvider } from 'react-intl';
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  MiddlewareFunction,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useRevalidator,
  useRouteLoaderData,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import {
  AvailabilityUnavailablePage,
  NotFoundPage,
  UnexpectedErrorPage,
} from '~/ui/error-page';
import { isAvailabilityUnavailable, requireOk } from '~/lib/react-router';
import { reportError } from '~/lib/rum';
import { shouldSkipRootRevalidation } from '~/routes/payment/should-skip-root-revalidation';
import { shouldRevalidateIgnoringBookingDialogParams } from '~/routes/checkout/booking/booking-dialog/revalidation';
import type { Route } from './+types/root';
import {
  consumePreviewHostQueryParam,
  getStorefrontByDomain,
  isPreviewHostOverrideActive,
  storefrontContext,
} from '~/domain/core/index.server';
import {
  consumePreviewQueryParam,
  isPreviewActive,
} from '~/domain/core/preview-cookie.server';
import {
  checkoutPolicyContext,
  createCheckoutPolicy,
} from '~/domain/pricing/index.server';
import { resolveDistributorFromRequest } from '~/domain/connect/index.server';
import {
  getLanguageCode,
  isValidLocale,
} from './lib/localization/languages.server';
import {
  loadMessages as loadTranslationMessages,
  translationMessagesContext,
} from './domain/i18n/messages.server';
import { generateNonce, nonceContext } from '~/domain/plugins/nonce.server';
import { createLogger, loggerContext } from './lib/logger';
import { awaitAll } from '~/lib/parallel';
import { ensureServerReady } from '~/platform/platform.server';
import {
  buildCustomFontHref,
  buildThemeOverrides,
} from './domain/theme/theme-overrides.server';
// Inter, self-hosted. Google's font CDN used to buy us a shared cross-site
// cache; cache partitioning killed that, so all it costs now is a second and
// third cold handshake (googleapis, then gstatic) in series ahead of first
// paint — and it hands a visitor's IP to Google from a customer's own branded
// domain. These two files carry the same axes the Google request did
// (`ital,opsz,wght`) and ship `font-display: swap` already. The family they
// register is `Inter Variable`; `panda.config.ts` points `fonts.default` at it.
import '@fontsource-variable/inter/standard.css';
import '@fontsource-variable/inter/standard-italic.css';
import './styles/globals.css';
import { securityHeadersMiddleware } from './lib/http-security-headers.server';
import { proxyBoundHostMiddleware } from '~/domain/websites/index.server';
import { CookieConsentScript } from '~/domain/plugins/services/cookie-consent-script';
import { AppEnvironment, SystemDefaultLocale } from './configuration';
import { PreviewBanner } from './ui/app-shell/preview-banner';
import { WARM_INTL } from '~/lib/warm-intl';
import { redirectDomainMiddleware } from './domain/redirects/redirect-domain-middleware.server';
import { keepTrackingParamsMiddleware } from './domain/redirects/keep-tracking-params-middleware.server';
import { handleMissingTranslations } from './lib/localization/missing-translations-handler';
import { Leaf } from '@understory-io/leaf/provider';
import { Header } from './ui/app-shell/header';
import { Footer } from './ui/app-shell/footer';
import { Page, PageCard } from './ui/app-shell/layouts';
import { ReactRouterLinkComponent } from './lib/react-router';
import {
  getMarketingProviders,
  marketingProvidersContext,
} from './domain/plugins/marketing-providers.server';
import { redirectBackwardsCompatiblePathsMiddleware } from './domain/redirects/backwards-compatibility.server';

/**
 * Promotes the tenant font stylesheet from `media="print"` to `media="all"`
 * once it has loaded, applying the font without ever having blocked rendering
 * on it. An inline `onload=` attribute is the usual way to write this, but our
 * CSP is nonce-based (`script-src 'self' 'nonce-…' 'strict-dynamic'`) and a
 * nonce does not whitelist inline event handlers — the browser would refuse to
 * run it, leaving the font permanently print-only. Hence a nonced script.
 *
 * A constant with no interpolation: nothing tenant-controlled reaches the
 * script body, and the markup is byte-identical on server and client.
 */
const ACTIVATE_TENANT_FONT = `
var link = document.querySelector('link[data-tenant-font]');
if (link.sheet) {
  link.media = 'all';
} else {
  link.addEventListener('load', function () { link.media = 'all'; }, { once: true });
}
`;

export function Layout({ children }: { children: React.ReactNode }) {
  const rootData = useRouteLoaderData<RootLoaderData>('root');

  return (
    // Full BCP-47 locale (e.g. `en-GB`), not the bare language — so the region
    // a tenant configured is declared to assistive tech and crawlers. Formatting
    // and strings already key off the same resolved locale (via IntlProvider /
    // loadMessages), so an `en-GB` tenant gets British spelling + formatting, an
    // `en-US` tenant American.
    // `data-trace-id` is how the trace reaches Dash0 without waiting for
    // React. entry.client.tsx reads it off the parsed document and hands it to
    // Dash0's init, so the page view and LCP carry it. Registering it from a
    // React effect instead (as the other tenant attributes do) would miss the
    // page view, and LCP whenever init comes first.
    <html
      lang={rootData?.locale ?? SystemDefaultLocale}
      data-trace-id={rootData?.traceId ?? undefined}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/*
         * Disable iOS Safari "Data Detectors". iOS auto-wraps phone numbers,
         * dates and addresses in <a> tags after the SSR HTML is parsed but
         * before React hydrates, causing hydration mismatches. Real contact
         * CTAs use explicit tel:/mailto: links, so opting out costs nothing.
         */}
        <meta
          name="format-detection"
          content="telephone=no, date=no, address=no, email=no"
        />
        <CookieConsentScript
          nonce={rootData?.nonce}
          language={rootData?.language}
          preview={rootData?.preview}
        />
        {/*
         * Keep inline scripts after the consent `<script src>`. Facebook's
         * iOS in-app browser inserts its own `<script src>` before the first
         * script on the page, so React hydrates each head script against the
         * element one before it. If an inline script comes first, the consent
         * fiber lands on its text and hydration fails (#418) for about a fifth
         * of in-app visits. Consent is production-only, so previews can't
         * show this.
         */}
        <script
          nonce={rootData?.nonce}
          dangerouslySetInnerHTML={{ __html: WARM_INTL }}
        />
        <Meta />
        <Links />
        {rootData?.customFontHref && (
          <>
            {/*
             * `media="print"` keeps the tenant's Google-hosted font off the
             * critical path: the browser still fetches it, but doesn't hold
             * first paint for it. `ACTIVATE_TENANT_FONT` flips it to `all`
             * once it lands. The URL already carries `&display=swap`, so text
             * paints in the fallback face and re-renders when the font swaps
             * in — the same behaviour as before, minus the blocking.
             *
             * A print stylesheet is otherwise fetched last, behind all of the
             * app's JavaScript; `fetchPriority="high"` keeps the font from
             * arriving later than it did when it came from Google.
             */}
            <link
              rel="stylesheet"
              href={rootData.customFontHref}
              media="print"
              fetchPriority="high"
              data-tenant-font=""
              suppressHydrationWarning
            />
            <script
              nonce={rootData.nonce}
              dangerouslySetInnerHTML={{ __html: ACTIVATE_TENANT_FONT }}
            />
          </>
        )}
      </head>
      <body>
        {AppEnvironment === 'preview' && rootData?.previewHostActive && (
          <PreviewBanner
            companyName={rootData.companyName}
            fqdn={rootData.fqdn}
          />
        )}
        {children}
        <ScrollRestoration nonce={rootData?.nonce} />
        <Scripts nonce={rootData?.nonce} />
      </body>
    </html>
  );
}

export type RootLoaderData = Route.ComponentProps['loaderData'];

/**
 * Read the root loader's data from any component rendered *below* the root
 * route. There, the data is always present, so this returns a non-null
 * `RootLoaderData` — unlike `useRouteLoaderData<RootLoaderData>('root')`, which
 * is typed `… | undefined` and forces a null guard (and a dead null-render
 * branch) at every call site.
 *
 * Do NOT use this in `root.tsx`'s own `Layout` / `ErrorBoundary`: those render
 * the document shell even when the root loader hasn't produced data (e.g. a
 * middleware throw), so they must keep the nullable `useRouteLoaderData`.
 */
export function useRootData(): RootLoaderData {
  const data = useRouteLoaderData<RootLoaderData>('root');
  if (!data) {
    throw new Error(
      'useRootData() must be called from a component rendered below the root route'
    );
  }
  return data;
}

export async function loader({ context, request, url }: Route.LoaderArgs) {
  const storefront = context.get(storefrontContext);
  const nonce = context.get(nonceContext);
  const logger = context.get(loggerContext);

  const { distributor, translationMessages, marketingProviders } =
    await awaitAll({
      distributor: resolveDistributorFromRequest(
        url,
        storefront.companyId,
        logger
      ),
      translationMessages: loadTranslationMessages(storefront.resolvedLocale),
      marketingProviders: getMarketingProviders(storefront.companyId),
    });
  const customization = distributor?.customization ?? storefront.customization;

  context.set(translationMessagesContext, translationMessages);
  context.set(marketingProvidersContext, marketingProviders);

  const colorMode = url.searchParams.get('color') as 'light' | 'dark' | null;

  return data({
    locale: storefront.resolvedLocale,
    language: getLanguageCode(storefront.resolvedLocale),
    companyName: storefront.companyProfile.name,
    companyLogo: storefront.companyProfile.logo,
    companyWebsite: storefront.companyProfile.website,
    companyEmail: storefront.companyProfile.email,
    companyPhone: storefront.companyProfile.phone,
    defaultCurrency: storefront.defaultCurrency,
    languages: storefront.languages,
    defaultLanguage: storefront.defaultLanguage,
    translationMessages,
    preferences: storefront.preferences,
    companyId: storefront.companyId,
    storefrontId: storefront.storefrontId,
    listedExperiencesCount: storefront.listedExperiences.length,
    fqdn: storefront.fqdn,
    nonce,
    preview: storefront.companyProfile.preview,
    themeOverrides: buildThemeOverrides(customization),
    customFontHref: buildCustomFontHref(customization),
    distributor,
    marketingProviders,
    previewHostActive: isPreviewHostOverrideActive(request),
    colorMode,
    // The trace that served this document, so a RUM signal can be joined to
    // the server work behind it. Without it a slow LCP names a page and a
    // tenant and nothing else — which is why "is the slow tail the BFF?" had
    // to be argued from circumstance rather than read off the trace.
    traceId: trace.getActiveSpan()?.spanContext().traceId ?? null,
  });
}

export default function App() {
  return <Outlet />;
}

export const middleware: MiddlewareFunction[] = [
  // Load secrets from Secrets Manager on the first request (memoized). Runs in
  // request scope so the Vercel OIDC token (delivered per request) is available
  // to assume the AWS role; must precede anything that reads secret-backed env
  // (e.g. the storefronts gRPC client below). Root middleware runs for every
  // route — including the top-level resource routes (GTM proxy, api/v2/*).
  async () => {
    await ensureServerReady();
  },
  keepTrackingParamsMiddleware,
  securityHeadersMiddleware,
  redirectDomainMiddleware,
  redirectBackwardsCompatiblePathsMiddleware,

  async ({ request, url, context }) => {
    const previewHostRedirect = consumePreviewHostQueryParam(url);
    if (previewHostRedirect) {
      throw previewHostRedirect;
    }

    // `?preview=true` persists customer preview mode to a cookie (and strips
    // the param), so the booking UI stays reachable before payment is set up
    // as the visitor navigates into checkout. See `isPreviewActive`.
    const previewRedirect = await consumePreviewQueryParam(request, url);
    if (previewRedirect) {
      throw previewRedirect;
    }

    const reqLogger = createLogger().child({
      method: request.method,
      path: url.pathname,
      query: url.search,
    });

    const firstSegment = url.pathname.split('/')[1];
    const firstSegmentIsValidLocale = isValidLocale(firstSegment);

    const storefront = requireOk(
      await getStorefrontByDomain(
        { request, url },
        firstSegmentIsValidLocale ? firstSegment : undefined
      ),
      'root: getStorefrontByDomain',
      reqLogger
    );

    if ('redirect' in storefront) {
      throw redirect(storefront.redirect, 308);
    }

    const tenantLogger = reqLogger.child({
      companyId: storefront.companyId,
      storefrontId: storefront.storefrontId,
      domain: storefront.fqdn,
      locale: storefront.resolvedLocale,
    });

    context.set(loggerContext, tenantLogger);
    context.set(storefrontContext, storefront);
    context.set(nonceContext, generateNonce());

    trace.getActiveSpan()?.setAttributes({
      'app.company_id': storefront.companyId,
      'app.storefront_id': storefront.storefrontId,
    });

    // Resolve the checkout access policy once per request from runtime inputs.
    // Preview (`?preview=true` persisted to a cookie, or a company flagged as
    // preview) is the only runtime input; the policy owns how it affects
    // checkout/payment access. Loaders read it via `checkoutPolicyContext`.
    const isPreview =
      (await isPreviewActive(request)) || storefront.companyProfile.preview;
    context.set(checkoutPolicyContext, createCheckoutPolicy({ isPreview }));
  },

  // On a website-bound host, hand every path the storefront doesn't own to the
  // websites product. Runs after tenant resolution (needs `storefrontContext` /
  // `loggerContext`) and ahead of route handling, so it proxies unmapped
  // sub-pages (e.g. `/about`) and sub-resources before the `:locale?` layout
  // loader can 404 them. Storefront routes and non-bound hosts fall through.
  proxyBoundHostMiddleware,
];

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  // The payment-callback page polls by revalidating in place; root data
  // (tenant/i18n/theme) is stable for the page's lifetime, so skip root's two
  // external calls on those poll ticks. Scoped to the callback path so
  // app-wide revalidation is unchanged.
  if (
    shouldSkipRootRevalidation(args.currentUrl.pathname, args.nextUrl.pathname)
  ) {
    return false;
  }
  // Booking-dialog open/close and in-dialog changes are search-param
  // navigations on the same page; this loader reads none of those params
  // (`distributorId` / `color` changes still revalidate), so don't let dialog
  // churn re-run the distributor/translations/marketing-providers fetches.
  return shouldRevalidateIgnoringBookingDialogParams(args);
}

export function links() {
  return [
    // Inter is self-hosted (see the `@fontsource-variable/inter` imports), but
    // a tenant's own `customization.fontFamily` still comes from Google — an
    // arbitrary family we can't bundle. Its stylesheet comes through our own
    // origin (`routes/api/fonts`); the font files it points at don't. Warm
    // that connection here, at the very top of `<head>`, so the DNS/TCP/TLS
    // cost overlaps our own CSS download instead of queueing behind it.
    // `fonts.gstatic.com` serves the woff2 under CORS, so its connection has
    // to be negotiated as anonymous or the browser opens a second one anyway.
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    },
    {
      rel: 'icon',
      href: '/favicon.png',
    },
  ];
}

// Diagnostic instrumentation for US-10442. React Router's Single Fetch throws
// `SingleFetchNoResultError` ("No result found for routeId ...") when a
// client-side navigation's `.data` response is missing a route the client
// expected. The RUM event carries no URL, so we can't tell which paths trigger
// it. When we recognise that error by message, attach the current location and
// the routeId parsed from the message so we can pinpoint the client/server
// route-match divergence. Returns `undefined` (no extra attributes) for every
// other error, and on the server where `window` is unavailable.
function noRouteResultDiagnostics(
  error: Error
): { attributes: Record<string, string> } | undefined {
  if (typeof window === 'undefined') return undefined;
  const routeId = /No result found for routeId "([^"]+)"/.exec(
    error.message
  )?.[1];
  if (!routeId) return undefined;
  return {
    attributes: {
      'app.no_route_result.route_id': routeId,
      'app.no_route_result.pathname': window.location.pathname,
      'app.no_route_result.url': window.location.href,
    },
  };
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const storefront = useRouteLoaderData<RootLoaderData>('root');
  const revalidator = useRevalidator();
  if (error && error instanceof Error) {
    reportError(error, noRouteResultDiagnostics(error));
  }

  return (
    // `Leaf` re-provides intl with its own `leaf.*` catalogue merged in, so it
    // belongs inside ours — see the note in `routes/layout.tsx`.
    <IntlProvider
      locale={storefront?.locale ?? SystemDefaultLocale}
      messages={storefront?.translationMessages}
      onError={handleMissingTranslations}
    >
      <Leaf
        theme="storefront"
        colorMode="light"
        themeOverrides={storefront?.themeOverrides}
        linkComponent={ReactRouterLinkComponent}
        locale={storefront?.locale ?? SystemDefaultLocale}
      >
        <Page>
          <PageCard>
            <Header
              companyName={storefront?.companyName}
              companyWebsite={storefront?.companyWebsite}
              logo={storefront?.companyLogo}
              config={{}}
            />
            {isRouteErrorResponse(error) && error.status === 404 ? (
              <NotFoundPage />
            ) : isAvailabilityUnavailable(error) ? (
              <AvailabilityUnavailablePage
                onRetry={() => void revalidator.revalidate()}
                retrying={revalidator.state === 'loading'}
              />
            ) : (
              <UnexpectedErrorPage
                stack={error instanceof Error ? error.stack : undefined}
              />
            )}
          </PageCard>
          <Footer
            layout="narrow"
            companyName={storefront?.companyName}
            companyEmail={storefront?.companyEmail}
            companyPhone={storefront?.companyPhone}
          />
        </Page>
      </Leaf>
    </IntlProvider>
  );
}
